import { t } from "~/i18n";

/**
 * Maps machine-readable auth error codes from platform-api (invite gating)
 * and Better Auth base codes to user-facing messages.
 */

/** Structural shape of a Better Auth client error (`{ data, error }`). */
export interface AuthClientError {
  code?: string | null;
  message?: string | null;
  status?: number;
}

/** i18next key for the fallback message when no error code is recognized. */
const GENERIC_AUTH_ERROR_KEY = "common/couldNotCompleteThe_kftbn0";

const ACCOUNT_EXISTS_KEY = "common/anAccountWithThis_gsszlk";
const INVITE_EMAIL_MISMATCH_KEY = "common/thisInviteWasSent_9vro9q";

/** i18next keys for machine-readable auth error codes. */
const AUTH_ERROR_KEYS: Record<string, string> = {
  INVITE_INVALID: "common/thisInviteIsNot_9zpc4p",
  INVITE_USED: "common/thisInviteHasAlready_8m17gy",
  INVITE_EXPIRED: "common/thisInviteHasExpired_57a2yy",
  INVITE_EMAIL_MISMATCH: INVITE_EMAIL_MISMATCH_KEY,
  INVALID_EMAIL_OR_PASSWORD: "common/incorrectEmailOrPassword_3y4151",
  USER_ALREADY_EXISTS: ACCOUNT_EXISTS_KEY,
  // Better Auth 1.6 answers a sign-up for a taken address with this code.
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: ACCOUNT_EXISTS_KEY,
  PASSWORD_TOO_SHORT: "common/passwordIsTooShort_eutvfs",
  INVALID_EMAIL: "common/enterAValidEmail_g00xac",
};

/** Returns the user-facing message for an auth failure. Never echoes tokens. */
export function authErrorMessage(error: AuthClientError | null | undefined): string {
  const key = error && typeof error.code === "string" ? AUTH_ERROR_KEYS[error.code] : undefined;
  return t(key ?? GENERIC_AUTH_ERROR_KEY);
}

/** The address already has an account: the way on is signing in to it. */
export function isExistingAccountError(error: AuthClientError | null | undefined): boolean {
  return error != null && AUTH_ERROR_KEYS[error.code ?? ""] === ACCOUNT_EXISTS_KEY;
}
